import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import * as logger from 'loglevel';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import useStyles from '@front10/helpers/dist/UseStyles';
import { Context } from '@front10/helpers/dist/Context';
import { getRelativeDateLocalization } from '../../../../helpers/localization/localizationUtils';

import Box from '../../../elements/Box';
import Text from '../../../elements/Text';

const FareItemLastSeen = (props) => {
  const {
    searchDate,
    showDate,
    showText,
    dateStyles,
    labelStyles,
    labelKey,
    labelKeyAgo,
    withoutSuffix,
    thresholds,
    debugMode = false
  } = props;

  dayjs.extend(relativeTime, {
    rounding: (value) => {
      if (debugMode) {
        logger.warn(`roundValue: ${value}`);
      }
      return Math.floor(value);
    },
    thresholds
  });

  const { direction, translate, localizationData, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  return (
    <Box className={`fare-item-view-date ${className}`} style={style}>
      {showText && (
        <Text paddingRight="1" floatLeft {...labelStyles}>
          {translate(labelKey, 'Viewed:')}
        </Text>
      )}
      {showDate && (
        <Text floatLeft {...dateStyles}>
          {dayjs()
            .locale(getRelativeDateLocalization(translate, localizationData), null, true)
            .to(searchDate, withoutSuffix)}
          {withoutSuffix && ` ${translate(labelKeyAgo, 'ago')} `}
        </Text>
      )}
    </Box>
  );
};

FareItemLastSeen.propTypes = {
  /**
   * Date to show
   */
  searchDate: PropTypes.string,
  /**
   * Show Visualization Date
   */
  showVisualizationDate: PropTypes.bool,
  /**
   * Show Date
   */
  showDate: PropTypes.bool,
  /**
   * Show Text
   */
  showText: PropTypes.bool,
  /**
   * dateStyles props for using into withStyle.
   */
  dateStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * labelStyles props for using into withStyle.
   */
  labelStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * labelKey props
   */
  labelKey: PropTypes.string,
  /**
   * If you pass true, you can get the value without the suffix.
   * false: in 2 years
   * true: 2 years
   */
  withoutSuffix: PropTypes.bool,
  /**
   * labelKey ago
   */
  labelKeyAgo: PropTypes.string
};

FareItemLastSeen.defaultProps = {
  showVisualizationDate: false,
  showDate: true,
  showText: true,
  searchDate: '',
  // TODO: add unicodeBidi prop to withStyles and create functional-css classes in our Front10-Bootstrap
  dateStyles: {
    style: {
      unicodeBidi: 'plaintext'
    }
  },
  labelStyles: {
    style: {
      unicodeBidi: 'plaintext'
    }
  },
  labelKey: 'cmp-viewed',
  withoutSuffix: true,
  labelKeyAgo: 'cmp-time-ago',
  thresholds: [
    { l: 's', r: 1 },
    { l: 'ss', r: 59, d: 'second' },
    { l: 'm', r: 1 },
    { l: 'mm', r: 59, d: 'minute' },
    { l: 'h', r: 1 },
    { l: 'hh', r: 23, d: 'hour' },
    { l: 'd', r: 1 },
    { l: 'dd', r: 29, d: 'day' },
    { l: 'M', r: 1 },
    { l: 'MM', r: 11, d: 'month' },
    { l: 'y', r: 1 },
    { l: 'yy', d: 'year' }
  ]
};

FareItemLastSeen.componentName = 'FareItemLastSeen';

export default FareItemLastSeen;
